/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import Text from 'src/components/ts-components/Text'
import { useRegistrationSetupContext } from '../../context'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import { Tooltip } from 'antd'
import SelectMultiInput from 'src/components/ts-components/SelectMultiInput'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { Link } from 'react-router-dom'
import { useEffect, useMemo, useCallback } from 'react'
import { useCountries } from '@sportninja/common/hooks/useCountries'
import { getLocaleFromCountry } from '@sportninja/common/currency'

const Payments = () => {
  const {
    state,
    setState,
    availableTaxes,
    apiAccount,
    registration,
    analytics,
  } = useRegistrationSetupContext()

  const { countries, refetch: fetchCountries } = useCountries()

  useEffect(() => {
    fetchCountries()
  }, [fetchCountries])

  const hasRegistrations = (analytics?.registrations?.total ?? 0) > 0

  const isSportsPay = String(state.payment_provider) === '2'

  const currencyCountryOptions = useMemo(() => {
    const currentId = String(state?.currency_country_id || '')
    const seen = new Set<string>()
    return countries
      .filter((c) => c.currency_code)
      .filter((c) => {
        if (!isSportsPay) {
          return true
        }
        const code = c.currency_code.toUpperCase()
        return code === 'USD' || code === 'CAD'
      })
      .slice()
      .sort((a, b) => {
        // Put the current currency_country first so it survives deduplication
        if (String(a.id) === currentId) {
          return -1
        }
        if (String(b.id) === currentId) {
          return 1
        }
        return 0
      })
      .filter((c) => {
        const code = c.currency_code.toUpperCase()
        if (seen.has(code)) {
          return false
        }
        seen.add(code)
        return true
      })
      .sort((a, b) => {
        const aCode = a.currency_code.toUpperCase()
        const bCode = b.currency_code.toUpperCase()
        if (aCode === 'USD') {
          return -1
        }
        if (bCode === 'USD') {
          return 1
        }
        if (aCode === 'CAD') {
          return -1
        }
        if (bCode === 'CAD') {
          return 1
        }
        return 0
      })
      .map((c) => ({
        label: `${c.currency_code.toUpperCase()} (${c.currency_symbol})`,
        value: String(c.id),
      }))
  }, [countries, state?.currency_country_id, isSportsPay])

  // Resolve locale from the selected currency country for formatting tax rates
  const selectedCountry = useMemo(() => {
    if (!state?.currency_country_id) {
      return undefined
    }
    return countries.find(
      (c) => String(c.id) === String(state.currency_country_id)
    )
  }, [countries, state?.currency_country_id])

  const formatTaxRate = useCallback(
    (rate: string | number) => {
      const locale = getLocaleFromCountry(selectedCountry)
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(
        Number(rate)
      )
    },
    [selectedCountry]
  )

  const paymentProviderOptions = useMemo(() => {
    if (registration?.payment_provider_type) {
      return registration?.payment_provider_type
        ? [
            {
              label: registration?.payment_provider_type?.name,
              value: registration?.payment_provider_type?.id,
            },
          ]
        : []
    }
    return apiAccount?.payment_provider_type
      ? [
          {
            label: apiAccount?.payment_provider_type?.name,
            value: apiAccount?.payment_provider_type?.id,
          },
        ]
      : []
  }, [apiAccount?.payment_provider_type, registration?.payment_provider_type])

  // Auto-select payment provider when there is only one option available
  useEffect(() => {
    if (paymentProviderOptions.length === 1 && !state.payment_provider) {
      setState((prev) => ({
        ...prev,
        payment_provider: paymentProviderOptions[0].value,
      }))
    }
  }, [paymentProviderOptions, state.payment_provider])

  return (
    <div
      css={css`
        display: flex;
        gap: 16px;
        flex-direction: column;
        align-items: flex-end;
        width: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Payments
        </Text>
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 16px;
          align-self: stretch;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <SelectInput
            label='Payment Provider'
            options={paymentProviderOptions}
            value={state.payment_provider || ''}
            onChange={(value) => {
              setState({
                ...state,
                payment_provider: value,
              })
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            disabled={false}
            allowClear={false}
            name='payment_provider'
            notFoundContent={
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background: transparent;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Set Up Your Payment Provider In{' '}
                  <Link
                    to={`${ROUTES.GENERAL_SETTINGS}?origin=payment_provider`}
                    target='_blank'
                    css={css`
                      color: ${colors.PRIMARY};
                    `}
                  >
                    Account Settings
                  </Link>
                </Text>
              </div>
            }
          />
        </div>
        <Tooltip
          title={
            hasRegistrations
              ? 'Currency cannot be changed after receiving registrations'
              : ''
          }
          zIndex={10010}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <SelectInput
              label='Currency'
              allowClear={false}
              showSearch={true}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={currencyCountryOptions}
              value={String(state?.currency_country_id || '')}
              onChange={(value) => {
                setState({
                  ...state,
                  currency_country_id: value,
                })
              }}
              fieldErrors={state.fieldErrors}
              customError={state.customError}
              disabled={hasRegistrations}
              name='currency_country_id'
            />
          </div>
        </Tooltip>
      </div>
      <SelectMultiInput
        label='Taxes'
        options={
          availableTaxes?.map((tax) => ({
            label: `${tax.name} (${formatTaxRate(tax.tax_rate)}%)`,
            value: tax.id,
          })) || []
        }
        value={state.taxes}
        onChange={(value) => {
          setState({
            ...state,
            taxes: value,
          })
        }}
        fieldErrors={state.fieldErrors}
        customError={state.customError}
        name='taxes'
      />
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          align-self: stretch;
          width: 100%;
        `}
      >
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
          style={'align-self: flex-end;'}
        >
          Taxes And Your Payment Provider Can Be Configured In The{' '}
          <Link
            to={`${ROUTES.GENERAL_SETTINGS}?origin=payment_provider`}
            target='_blank'
            css={css`
              color: ${colors.PRIMARY};
            `}
          >
            Account Settings.
          </Link>
        </Text>
      </div>
    </div>
  )
}

export default Payments
